/* ==========================================================================
   Site search — an instant, typo-tolerant destination index plus
   server-rendered print suggestions.

   Why this exists rather than a plain predictive-search dropdown: Shopify's
   /search/suggest is prefix-matched per word and has no typo tolerance, so on
   this catalogue "roma" returns Romania, and "nyc", "tokio" and "big apple"
   return nothing at all. Destinations are therefore matched LOCALLY against an
   index seeded into the page by Liquid — which also means the first keystroke
   paints in the same frame, with no network in the path. That is the whole
   trick behind the feel; no server round trip gets under ~300ms.

   Two tiers:
     Tier 0  destinations — local, synchronous, fuzzy. No debounce: debouncing
             is a network affordance and there is no network here.
     Tier 1  prints — /search/suggest rendered through our own Liquid section,
             debounced and abortable, so prices come back correctly formatted
             in the buyer's currency instead of a bare "18.00".
   ========================================================================== */
(function () {
  'use strict';

  /* --- Folding ----------------------------------------------------------
     Emits the normalized string AND a map back to original offsets, because
     matching happens on the folded form while highlighting has to paint the
     original ("Zürich", "Antigua & Barbuda", "Martha's Vineyard"). */
  var CHAR_MAP = {
    'ø': 'o', 'æ': 'ae', 'œ': 'oe', 'ß': 'ss', 'ł': 'l', 'đ': 'd', 'ð': 'd',
    'þ': 'th', 'ı': 'i', 'İ': 'i', '’': '', "'": '', '‘': '', '´': '', '`': '',
    '&': ' and ', '–': ' ', '—': ' '
  };
  var HAS_NORMALIZE = typeof ''.normalize === 'function';
  var COMBINING = /[̀-ͯ]/g;

  function fold(str) {
    var src = String(str == null ? '' : str);
    var out = '';
    var map = [];
    var lastWasSpace = true;
    for (var i = 0; i < src.length; i++) {
      var ch = src[i];
      var rep = CHAR_MAP[ch];
      if (rep === undefined) {
        rep = ch.toLowerCase();
        if (HAS_NORMALIZE) {
          try {
            rep = rep.normalize('NFD').replace(COMBINING, '');
          } catch (e) {
            /* Old WebViews: fall through with the un-decomposed char. */
          }
        }
      }
      for (var k = 0; k < rep.length; k++) {
        var c = rep[k];
        var isWord = (c >= 'a' && c <= 'z') || (c >= '0' && c <= '9');
        if (isWord) {
          out += c;
          map.push(i);
          lastWasSpace = false;
        } else if (!lastWasSpace) {
          out += ' ';
          map.push(i);
          lastWasSpace = true;
        }
      }
    }
    while (out.length && out[out.length - 1] === ' ') {
      out = out.slice(0, -1);
      map.pop();
    }
    return { norm: out, map: map };
  }

  var TOKEN_CANON = { st: 'saint', 'st.': 'saint', ste: 'sainte', mt: 'mount', mnt: 'mount', ft: 'fort' };

  function canonTokens(norm) {
    if (!norm) return [];
    var raw = norm.split(' ');
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      if (!raw[i]) continue;
      out.push(TOKEN_CANON[raw[i]] || raw[i]);
    }
    return out;
  }

  /* Product nouns and function words a shopper wraps a place name in.
     Stripped from the EDGES only — a place name is never a mid-query filler. */
  var NOISE = {};
  ('print prints printed poster posters art arts artwork artworks wall decor decoration canvas photo photos ' +
   'photograph photography picture pictures painting paintings map maps illustration illustrations framed ' +
   'frame gift gifts skyline travel vintage retro minimalist collection of in the a an for my me and to buy cheap')
    .split(' ').forEach(function (w) { NOISE[w] = true; });

  function stripEdgeNoise(tokens) {
    var a = 0;
    var b = tokens.length;
    while (a < b && NOISE[tokens[a]]) a++;
    while (b > a && NOISE[tokens[b - 1]]) b--;
    return tokens.slice(a, b);
  }

  function letterMask(norm) {
    var m = 0;
    for (var i = 0; i < norm.length; i++) {
      var c = norm.charCodeAt(i) - 97;
      if (c >= 0 && c < 26) m |= (1 << c);
    }
    return m;
  }

  function popcount(x) {
    x = x - ((x >> 1) & 0x55555555);
    x = (x & 0x33333333) + ((x >> 2) & 0x33333333);
    x = (x + (x >> 4)) & 0x0f0f0f0f;
    return (x * 0x01010101) >> 24;
  }

  /* --- Banded OSA (restricted Damerau-Levenshtein) ----------------------
     Damerau rather than plain Levenshtein because transposition is the
     dominant typo class: "new yrok" is one edit under OSA and two under
     Levenshtein, and at length 4 the budget is 1 — so plain Levenshtein
     rejects the single most obvious demo case. Fixed module-level buffers so
     the hot path never allocates. */
  var BUF_A = new Int32Array(64);
  var BUF_B = new Int32Array(64);
  var BUF_C = new Int32Array(64);

  function osaWithin(a, b, max) {
    if (max < 0) return 1;
    var alen = a.length;
    var blen = b.length;
    if (alen > blen) { var t = a; a = b; b = t; t = alen; alen = blen; blen = t; }
    if (blen - alen > max) return max + 1;
    if (alen === 0) return blen <= max ? blen : max + 1;

    var start = 0;
    while (start < alen && a.charCodeAt(start) === b.charCodeAt(start)) start++;
    if (start) { a = a.slice(start); b = b.slice(start); alen -= start; blen -= start; }
    var end = 0;
    while (end < alen && a.charCodeAt(alen - 1 - end) === b.charCodeAt(blen - 1 - end)) end++;
    if (end) { a = a.slice(0, alen - end); b = b.slice(0, blen - end); alen -= end; blen -= end; }
    if (alen === 0) return blen <= max ? blen : max + 1;
    if (blen > 62) return max + 1;

    var prev2 = BUF_A;
    var prev = BUF_B;
    var cur = BUF_C;
    var swap;
    for (var j = 0; j <= blen; j++) prev[j] = j;
    for (var i = 1; i <= alen; i++) {
      cur[0] = i;
      var lo = i - max; if (lo < 1) lo = 1;
      var hi = i + max; if (hi > blen) hi = blen;
      if (lo > 1) cur[lo - 1] = max + 1;
      var rowMin = max + 1;
      var ca = a.charCodeAt(i - 1);
      for (var j2 = lo; j2 <= hi; j2++) {
        var cb = b.charCodeAt(j2 - 1);
        var v = prev[j2 - 1] + (ca === cb ? 0 : 1);
        var del = prev[j2] + 1; if (del < v) v = del;
        var ins = cur[j2 - 1] + 1; if (ins < v) v = ins;
        if (i > 1 && j2 > 1 && ca === b.charCodeAt(j2 - 2) && a.charCodeAt(i - 2) === cb) {
          var tr = prev2[j2 - 2] + 1;
          if (tr < v) v = tr;
        }
        cur[j2] = v;
        if (v < rowMin) rowMin = v;
      }
      if (hi < blen) cur[hi + 1] = max + 1;
      if (rowMin > max) return max + 1;
      swap = prev2; prev2 = prev; prev = cur; cur = swap;
    }
    var d = prev[blen];
    return d > max ? max + 1 : d;
  }

  function maxDistFor(len) {
    if (len <= 3) return 0;
    if (len <= 6) return 1;
    if (len <= 10) return 2;
    return 3;
  }

  /* --- Index ------------------------------------------------------------ */
  function makeEntry(title, handle, count, region, curated) {
    var f = fold(title);
    var tokens = canonTokens(f.norm);
    var squash = f.norm.replace(/ /g, '');
    var initials = '';
    for (var i = 0; i < tokens.length; i++) initials += tokens[i][0];
    return {
      title: title,
      handle: handle,
      count: count || 0,
      region: region || '',
      regionNorm: region ? fold(region).norm : '',
      curated: !!curated,
      norm: f.norm,
      map: f.map,
      tokens: tokens,
      squash: squash,
      initials: tokens.length > 1 ? initials : '',
      mask: letterMask(f.norm)
    };
  }

  /* --- Scoring ----------------------------------------------------------
     One 0-1000 number line. Whole-query tiers start at 800 so that no
     combination of per-term tiers (capped at 790) can ever outrank the
     weakest whole-query match — the ordering is structural, not tuned. */
  function wholeQueryTier(e, q, aliases) {
    if (!q) return 0;
    if (e.norm === q) return 1000;
    if (aliases[q] === e.handle || e.handle === q) return 960;
    if (e.squash === q.replace(/ /g, '')) return 920;
    if (e.norm.indexOf(q) === 0) return 880;
    if (q.length >= 4 && e.squash.indexOf(q.replace(/ /g, '')) === 0) return 850;
    if (e.handle.indexOf(q) === 0) return 840;
    for (var i = 1; i < e.tokens.length; i++) {
      if (e.tokens[i].indexOf(q) === 0) return 800;
    }
    return 0;
  }

  function termTier(e, term, qMask) {
    var i;
    for (i = 0; i < e.tokens.length; i++) if (e.tokens[i] === term) return 100;
    if (e.norm.indexOf(term) === 0) return 92;
    for (i = 0; i < e.tokens.length; i++) if (e.tokens[i].indexOf(term) === 0) return 84;
    if (e.initials && e.initials === term) return 78;
    if (e.squash.indexOf(term) === 0) return 74;
    if (term.length >= 4) {
      for (i = 0; i < e.tokens.length; i++) {
        var at = e.tokens[i].indexOf(term);
        if (at > 0) return 62;
      }
      if (e.norm.indexOf(term) > 0) return 56;
      if (e.squash.indexOf(term) > 0) return 52;
    }

    // Fuzzy only once nothing structural fired — a guess must never outrank
    // a certainty, so these tiers sit below substring-in-word (62).
    var max = maxDistFor(term.length);
    if (max > 0) {
      // Letter-set lower bound: every letter in the query absent from the
      // candidate costs at least one edit. Pure bitwise, ~2ns.
      if (popcount(qMask & ~e.mask) <= max) {
        for (i = 0; i < e.tokens.length; i++) {
          var tok = e.tokens[i];
          if (max >= 2 && tok.charCodeAt(0) !== term.charCodeAt(0)) continue;
          var d = osaWithin(term, tok, max);
          if (d <= max) return 60 - 12 * d;
        }
        if (term.length >= 6) {
          var ds = osaWithin(term, e.squash, max);
          if (ds <= max) return 56 - 12 * ds;
        }
        if (term.length >= 5) {
          var head = e.squash.slice(0, term.length + max);
          var dp = osaWithin(term, head, max);
          if (dp <= max) return 52 - 12 * dp;
        }
      }
    }

    // Subsequence — the consonant-skeleton rung ("brcln" -> Barcelona). The
    // density floor is what stops naive subsequence search from returning
    // scattered-letter garbage.
    if (term.length >= 4) {
      var pos = -1;
      var first = -1;
      var last = -1;
      var found = 0;
      for (i = 0; i < term.length; i++) {
        pos = e.norm.indexOf(term[i], pos + 1);
        if (pos === -1) break;
        if (first === -1) first = pos;
        last = pos;
        found++;
      }
      if (found === term.length) {
        var density = term.length / (last - first + 1);
        if (density >= 0.5) {
          var atWordStart = first === 0 || e.norm[first - 1] === ' ';
          return Math.min(48, 28 + 14 * density + (atWordStart ? 6 : 0));
        }
      }
    }
    return 0;
  }

  function scoreEntry(e, parsed, aliases) {
    var w = wholeQueryTier(e, parsed.joined, aliases);

    var sum = 0;
    var wsum = 0;
    var matched = 0;
    var terms = parsed.terms;
    for (var i = 0; i < terms.length; i++) {
      var term = terms[i];
      var weight = Math.max(2, term.length);
      var t = termTier(e, term, parsed.masks[i]);
      if (t < 30 && e.regionNorm) {
        // Country context ("paris france") — secondary field, discounted, and
        // never strong enough to let "france" alone outrank the France page.
        if (e.regionNorm.indexOf(term) === 0) t = Math.max(t, 84 * 0.7);
      }
      if (t >= 30) matched++;
      sum += t * weight;
      wsum += weight;
    }
    var combined = 0;
    if (wsum > 0) {
      var coverage = matched / terms.length;
      combined = (sum / wsum) * (0.35 + 0.65 * coverage) * 7.9;
    }

    var score = Math.max(w, combined);
    if (score <= 0) return 0;
    if (matched === terms.length && terms.length > 0) score += 6;
    if (e.curated) score += 4;
    if (terms.length === e.tokens.length) score += 3;
    return score;
  }

  function parseQuery(raw) {
    var f = fold(raw);
    var tokens = canonTokens(f.norm);
    if (!tokens.length) return null;
    var stripped = stripEdgeNoise(tokens);
    // "wall art", "cheap posters" — every token is noise and there is more
    // than one, so the shopper is not naming a place.
    if (!stripped.length) {
      if (tokens.length > 1) return null;
      stripped = tokens;
    }
    var masks = [];
    for (var i = 0; i < stripped.length; i++) masks.push(letterMask(stripped[i]));
    return { joined: stripped.join(' '), terms: stripped, masks: masks, rawJoined: f.norm };
  }

  function search(index, raw, aliases, limit) {
    var parsed = parseQuery(raw);
    if (!parsed) return [];
    var out = [];
    var best = 0;
    for (var i = 0; i < index.length; i++) {
      var s = scoreEntry(index[i], parsed, aliases);
      if (s >= 300) {
        if (s > best) best = s;
        out.push({ entry: index[i], score: s });
      }
    }
    // Relative cutoff: a strong hit collapses the list to the strong hits.
    // This is what stops fuzzy from feeling like noise.
    var floor = best * 0.62;
    out = out.filter(function (r) { return r.score >= floor; });
    out.sort(function (a, b) {
      return (b.score - a.score) ||
             (b.entry.count - a.entry.count) ||
             (a.entry.title.length - b.entry.title.length) ||
             (a.entry.title < b.entry.title ? -1 : 1);
    });
    return out.slice(0, limit || 5);
  }

  /* Ranges in ORIGINAL coordinates, for highlighting. */
  function matchRanges(entry, parsed) {
    if (!parsed) return [];
    var ranges = [];
    var seen = {};
    for (var i = 0; i < parsed.terms.length; i++) {
      var term = parsed.terms[i];
      if (seen[term] || term.length < 2) continue;
      seen[term] = true;
      var at = entry.norm.indexOf(term);
      if (at === -1) continue;
      var s = entry.map[at];
      var e = entry.map[at + term.length - 1];
      if (s == null || e == null) continue;
      ranges.push({ start: s, end: e + 1 });
    }
    ranges.sort(function (a, b) { return a.start - b.start; });
    var merged = [];
    for (var k = 0; k < ranges.length; k++) {
      var last = merged[merged.length - 1];
      if (last && ranges[k].start <= last.end) last.end = Math.max(last.end, ranges[k].end);
      else merged.push(ranges[k]);
    }
    return merged.slice(0, 12);
  }

  function renderHighlighted(target, text, ranges) {
    var frag = document.createDocumentFragment();
    var pos = 0;
    for (var i = 0; i < ranges.length; i++) {
      var r = ranges[i];
      if (r.start > pos) frag.appendChild(document.createTextNode(text.slice(pos, r.start)));
      var hit = document.createElement('span');
      hit.className = 'ss-hit';
      hit.textContent = text.slice(r.start, r.end);
      frag.appendChild(hit);
      pos = r.end;
    }
    if (pos < text.length) frag.appendChild(document.createTextNode(text.slice(pos)));
    target.textContent = '';
    target.appendChild(frag);
  }

  /* --- Shared collection index ------------------------------------------
     One promise per page: the hero, the header drawer and the globe all want
     the same list, and it must not be fetched three times. */
  var indexPromise = null;

  /* Session cache. The homepage hero seeds the whole catalogue from Liquid;
     writing it here means the header drawer on every subsequent page gets the
     same instant, typo-tolerant matching without re-fetching or re-seeding.
     sessionStorage rather than localStorage on purpose: it dies with the tab,
     which caps staleness at one visit for free — this merchant adds
     destination collections continuously. */
  /* Recent searches live in localStorage (they should outlast the tab) while
     the catalogue lives in sessionStorage (it should not). */
  var RECENT_KEY = 'eastcoast:recent-searches';
  var RECENT_MAX = 5;

  function readRecent() {
    try {
      var parsed = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter(function (t) { return typeof t === 'string' && t; }) : [];
    } catch (e) {
      return [];
    }
  }

  function writeRecent(list) {
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) {
      /* Private mode / blocked storage: recent searches just do not persist. */
    }
  }

  var CACHE_KEY = 'eastcoast:destinations:v1';
  var CACHE_TTL = 12 * 60 * 60 * 1000;

  function readCache() {
    try {
      var raw = window.sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.rows || Date.now() - parsed.ts > CACHE_TTL) return null;
      return parsed.rows;
    } catch (e) {
      // Safari private mode and in-app WebViews throw on access. A storage
      // failure is a cache miss, never an error the shopper sees.
      return null;
    }
  }

  function writeCache(rows) {
    try {
      window.sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), rows: rows }));
    } catch (e) {
      /* no-op */
    }
  }

  function topUpCollections(rootUrl, seededHandles) {
    if (indexPromise) return indexPromise;
    indexPromise = new Promise(function (resolve) {
      var extra = [];
      var base = (rootUrl === '/' ? '' : rootUrl) + '/collections.json?limit=250';
      function page(n) {
        // /collections.json caps at 250 with no warning; this catalogue is
        // already past that, so paging is not optional.
        if (n > 3) return resolve(extra);
        fetch(base + '&page=' + n, { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            var list = (data && data.collections) || [];
            for (var i = 0; i < list.length; i++) {
              var c = list[i];
              if (!c.handle || seededHandles[c.handle]) continue;
              if (!c.products_count) continue;
              seededHandles[c.handle] = true;
              extra.push(makeEntry(c.title, c.handle, c.products_count, '', false));
            }
            if (list.length === 250) page(n + 1);
            else resolve(extra);
          })
          .catch(function () { resolve(extra); });
      }
      page(1);
    });
    return indexPromise;
  }

  /* ====================================================================== */
  function initInstance(root) {
    if (!root || root.__ssInit) return;
    root.__ssInit = true;

    var configTag = root.querySelector('[data-ss-config]');
    if (!configTag) return;
    var config;
    try {
      config = JSON.parse(configTag.textContent);
    } catch (e) {
      return;
    }

    var input = root.querySelector('[data-ss-input]');
    var panel = root.querySelector('[data-ss-panel]');
    var listbox = root.querySelector('[data-ss-listbox]');
    var idle = root.querySelector('[data-ss-idle]');
    var status = root.querySelector('[data-ss-status]');
    var clearBtn = root.querySelector('[data-ss-clear]');
    var correction = root.querySelector('[data-ss-correction]');
    var form = root.querySelector('form');
    if (!input || !panel || !listbox) return;

    var strings = config.strings || {};
    var aliases = config.aliases || {};
    var searchUrl = config.searchUrl || '/search';
    var suggestUrl = config.suggestUrl || '/search/suggest';
    var productSection = config.productSection || 'predictive-search-results';
    var maxDestinations = config.maxDestinations || 5;

    var index = [];
    var handles = {};
    var rows = (config.collections || []).slice();
    // A page without a seed (the header drawer on an inner page) picks the
    // catalogue up from the session cache the homepage wrote.
    var cached = readCache();
    if (cached) rows = rows.concat(cached);
    rows.forEach(function (row) {
      // Tuples, not objects: 30% smaller over the wire for no readability
      // cost in a machine-read blob. [title, handle, count, region, curated]
      if (!row || !row[0] || !row[1]) return;
      if (handles[row[1]]) return;
      handles[row[1]] = true;
      index.push(makeEntry(row[0], row[1], row[2], row[3], row[4]));
    });
    if (rows.length && !cached) {
      writeCache(rows.map(function (r) { return [r[0], r[1], r[2]]; }));
    }

    var options = [];
    var activeIndex = -1;
    var open = false;
    var pending = null;
    var suggestTimer = null;
    var frame = null;
    var lastQuery = '';
    var composing = false;
    var toppedUp = false;
    var productHtmlCache = {};

    var recentWrap = root.querySelector('[data-ss-recent]');
    var recentList = root.querySelector('[data-ss-recent-list]');
    var clearRecentBtn = root.querySelector('[data-ss-clear-recent]');

    function renderRecent() {
      if (!config.rememberRecent || !recentWrap || !recentList) return;
      var terms = readRecent();
      recentList.textContent = '';
      terms.forEach(function (term) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.className = 'ss__chip';
        a.textContent = term;
        a.href = searchUrl + '?q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last';
        li.appendChild(a);
        recentList.appendChild(li);
      });
      recentWrap.hidden = terms.length === 0;
    }

    function rememberTerm(term) {
      if (!config.rememberRecent || !term) return;
      var terms = readRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
      terms.unshift(term);
      writeRecent(terms);
    }

    function announce(msg) {
      if (status) status.textContent = msg || '';
    }

    function setOpen(next) {
      if (open === next) return;
      open = next;
      panel.hidden = !next;
      input.setAttribute('aria-expanded', next ? 'true' : 'false');
      root.classList.toggle('is-open', next);
      // Lets the hero dim its own background without this file knowing how.
      var host = root.closest('[data-ss-host]');
      if (host) host.classList.toggle('ss-host--open', next);
      if (!next) {
        activeIndex = -1;
        input.removeAttribute('aria-activedescendant');
      }
    }

    function collectOptions() {
      // Products arrive ~200ms after destinations. Re-collecting must keep the
      // option the shopper already arrowed onto, or their selection silently
      // vanishes the moment the network answers.
      var activeId = options[activeIndex] ? options[activeIndex].id : null;
      options = [].slice.call(listbox.querySelectorAll('[role="option"]'));
      options.forEach(function (o, i) {
        if (!o.id) o.id = 'ss-opt-' + i;
        o.setAttribute('aria-selected', 'false');
        o.classList.remove('is-active');
        o.tabIndex = -1;
      });
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
      if (activeId) {
        for (var i = 0; i < options.length; i++) {
          if (options[i].id === activeId) { setActive(i); return; }
        }
      }
    }

    function setActive(i) {
      if (options[activeIndex]) {
        options[activeIndex].classList.remove('is-active');
        options[activeIndex].setAttribute('aria-selected', 'false');
      }
      activeIndex = i;
      var opt = options[activeIndex];
      if (!opt) {
        input.removeAttribute('aria-activedescendant');
        return;
      }
      opt.classList.add('is-active');
      opt.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', opt.id);
      if (opt.scrollIntoView) opt.scrollIntoView({ block: 'nearest' });
    }

    function el(tag, cls, text) {
      var n = document.createElement(tag);
      if (cls) n.className = cls;
      if (text != null) n.textContent = text;
      return n;
    }

    function destinationGroup(results, parsed) {
      var group = el('div', 'ss-group');
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', strings.destinations || 'Destinations');
      var heading = el('p', 'ss-group__heading', strings.destinations || 'Destinations');
      heading.setAttribute('aria-hidden', 'true');
      var count = el('span', 'ss-group__count', String(results.length));
      heading.appendChild(count);
      group.appendChild(heading);

      var rows = el('div', 'ss-rows');
      results.forEach(function (r, i) {
        var e = r.entry;
        var a = document.createElement('a');
        a.className = 'ss-option ss-option--destination';
        a.id = 'ss-dest-' + i;
        a.setAttribute('role', 'option');
        a.setAttribute('aria-selected', 'false');
        a.tabIndex = -1;
        // Keeps locally-matched clicks visible in the merchant's search
        // analytics, which otherwise lose every destination click.
        a.href = '/collections/' + e.handle + '?_ss=e&_psq=' + encodeURIComponent(parsed ? parsed.joined : '');

        var pin = el('span', 'ss-option__pin');
        pin.setAttribute('aria-hidden', 'true');
        a.appendChild(pin);

        var body = el('span', 'ss-option__body');
        var title = el('span', 'ss-option__title');
        renderHighlighted(title, e.title, matchRanges(e, parsed));
        body.appendChild(title);
        if (e.region) body.appendChild(el('span', 'ss-option__meta', e.region));
        a.appendChild(body);

        if (e.count) {
          a.appendChild(el('span', 'ss-option__count',
            e.count + ' ' + (e.count === 1 ? (strings.printOne || 'print') : (strings.printOther || 'prints'))));
        }
        // Announced name stays the clean title regardless of the inline spans.
        a.setAttribute('aria-label', e.title + (e.count ? ', ' + e.count + ' ' + (strings.printOther || 'prints') : ''));
        rows.appendChild(a);
      });
      group.appendChild(rows);
      return group;
    }

    function viewAllOption(query) {
      var a = document.createElement('a');
      a.className = 'ss-option ss-option--all';
      a.id = 'ss-all';
      a.setAttribute('role', 'option');
      a.setAttribute('aria-selected', 'false');
      a.tabIndex = -1;
      a.href = searchUrl + '?q=' + encodeURIComponent(query) + '&options%5Bprefix%5D=last';
      a.appendChild(el('span', null, (strings.viewAll || 'See all results for “[[terms]]”').replace('[[terms]]', query)));
      return a;
    }

    function render(query) {
      var parsed = parseQuery(query);
      var results = parsed ? search(index, query, aliases, maxDestinations) : [];

      listbox.textContent = '';
      if (correction) correction.hidden = true;

      if (results.length) {
        listbox.appendChild(destinationGroup(results, parsed));
        // "Did you mean": only when nothing matched structurally, so the top
        // hit came from the fuzzy tiers and the shopper's spelling differs.
        var top = results[0].entry;
        if (correction && parsed && top.norm.indexOf(parsed.joined) === -1 && parsed.joined.length >= 4) {
          correction.hidden = false;
          correction.textContent = (strings.showingFor || 'Showing results for [[title]].').replace('[[title]]', top.title);
        }
      }

      var productSlot = el('div', 'ss-products');
      productSlot.setAttribute('data-ss-products', '');
      listbox.appendChild(productSlot);

      listbox.appendChild(viewAllOption(query));
      if (idle) idle.hidden = true;
      setOpen(true);
      collectOptions();

      var cached = productHtmlCache[query];
      if (cached != null) applyProducts(cached, query);

      announce(results.length
        ? (results.length === 1 ? (strings.countOne || '1 destination') : (strings.countOther || '[[count]] destinations').replace('[[count]]', results.length))
        : (strings.noDestinations || 'No destination matches. Press Enter to search all prints.'));
    }

    function applyProducts(html, query) {
      if (input.value.trim() !== query) return;
      var slot = listbox.querySelector('[data-ss-products]');
      if (!slot) return;
      // Safe: this HTML came from our own Liquid section, auto-escaped.
      slot.innerHTML = html;
      collectOptions();
    }

    function fetchProducts(query) {
      if (pending) pending.abort();
      pending = new AbortController();
      var controller = pending;
      var url = suggestUrl +
        '?q=' + encodeURIComponent(query) +
        '&section_id=' + encodeURIComponent(productSection) +
        '&resources[type]=product' +
        // Without limit_scope=each the limit is a shared pool across types.
        '&resources[limit]=6&resources[limit_scope]=each' +
        '&resources[options][unavailable_products]=last';

      fetch(url, { signal: controller.signal })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          if (controller !== pending) return;
          productHtmlCache[query] = html;
          applyProducts(html, query);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          // Per-tier degradation: destinations are local and still fine.
          var slot = listbox.querySelector('[data-ss-products]');
          if (slot) {
            slot.textContent = '';
            slot.appendChild(el('p', 'ss-note', strings.productsUnavailable || ''));
          }
        });
    }

    function onInput() {
      if (composing) return;
      var query = input.value.trim();
      if (clearBtn) clearBtn.hidden = query.length === 0;

      if (!query) {
        clearTimeout(suggestTimer);
        if (pending) pending.abort();
        listbox.textContent = '';
        if (correction) correction.hidden = true;
        renderRecent();
        if (idle) idle.hidden = false;
        setOpen(true);
        collectOptions();
        announce('');
        return;
      }

      lastQuery = query;
      // Coalesce the paint: a fast typist fires several input events inside
      // one frame, and painting lists nobody sees is the only real cost.
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(function () {
        frame = null;
        render(lastQuery);
      });

      clearTimeout(suggestTimer);
      if (query.length >= 2) {
        suggestTimer = setTimeout(function () { fetchProducts(query); }, 180);
      }

      if (!toppedUp) {
        toppedUp = true;
        topUpCollections(config.rootUrl || '/', handles).then(function (extra) {
          if (!extra.length) return;
          index = index.concat(extra);
          writeCache(index.map(function (e) { return [e.title, e.handle, e.count]; }));
          if (input.value.trim()) render(input.value.trim());
        });
      }
    }

    input.addEventListener('compositionstart', function () { composing = true; });
    input.addEventListener('compositionend', function () { composing = false; onInput(); });
    input.addEventListener('input', onInput);

    input.addEventListener('focus', function () {
      if (!input.value.trim()) {
        renderRecent();
        if (idle) idle.hidden = false;
        setOpen(true);
        collectOptions();
      } else {
        render(input.value.trim());
      }
      if (!toppedUp) {
        toppedUp = true;
        topUpCollections(config.rootUrl || '/', handles).then(function (extra) {
          if (extra.length) index = index.concat(extra);
        });
      }
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!options.length) return;
        e.preventDefault();
        var next = e.key === 'ArrowDown' ? activeIndex + 1 : activeIndex - 1;
        if (next >= options.length) next = 0;
        if (next < 0) next = options.length - 1;
        setActive(next);
        return;
      }
      if (e.key === 'Enter') {
        if (activeIndex >= 0 && options[activeIndex] && options[activeIndex].href) {
          e.preventDefault();
          window.location.href = options[activeIndex].href;
        }
        return;
      }
      if (e.key === 'Escape') {
        if (open) {
          // preventDefault matters: the browser's native Escape on an
          // <input type="search"> clears the field, which fires input and
          // re-opens the panel on the idle state.
          e.preventDefault();
          e.stopPropagation();
          setOpen(false);
        }
        return;
      }
      if (e.key === 'Home' && activeIndex >= 0) { e.preventDefault(); setActive(0); }
      if (e.key === 'End' && activeIndex >= 0) { e.preventDefault(); setActive(options.length - 1); }
    });

    listbox.addEventListener('mousemove', function (e) {
      var opt = e.target.closest('[role="option"]');
      if (!opt) return;
      var i = options.indexOf(opt);
      if (i >= 0 && i !== activeIndex) setActive(i);
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        input.value = '';
        clearBtn.hidden = true;
        onInput();
        input.focus();
      });
    }

    document.addEventListener('pointerdown', function (e) {
      // Must not preventDefault: a click on a link below the hero should
      // follow that link, not be consumed as a dismissal.
      if (open && !root.contains(e.target)) setOpen(false);
    });

    if (form) {
      form.addEventListener('submit', function () {
        rememberTerm(input.value.trim());
        setOpen(false);
      });
    }

    listbox.addEventListener('click', function (e) {
      if (e.target.closest('[role="option"]')) rememberTerm(input.value.trim());
    });

    if (clearRecentBtn) {
      clearRecentBtn.addEventListener('click', function () {
        writeRecent([]);
        renderRecent();
        // The button just became display:none, so it is already blurred;
        // blur() here would leave focus on <body>.
        input.focus({ preventScroll: true });
      });
    }

    renderRecent();

    // "/" focuses search, the way every search-first site behaves.
    document.addEventListener('keydown', function (e) {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      var t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (!root.offsetParent) return;
      e.preventDefault();
      input.focus();
      input.select();
    });

    // Warm the rest of the catalogue during idle time. Deliberately not on
    // DOMContentLoaded: the hero is above the fold and this must not compete
    // with LCP imagery.
    var warm = function () {
      if (toppedUp) return;
      toppedUp = true;
      topUpCollections(config.rootUrl || '/', handles).then(function (extra) {
        if (!extra.length) return;
        index = index.concat(extra);
        writeCache(index.map(function (e) { return [e.title, e.handle, e.count]; }));
      });
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(warm, { timeout: 4000 });
    } else {
      setTimeout(warm, 2500);
    }

    root.__ssTeardown = function () {
      clearTimeout(suggestTimer);
      if (pending) pending.abort();
      if (frame) cancelAnimationFrame(frame);
    };
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-ss]').forEach(initInstance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // The theme editor replaces the whole section node on every settings change;
  // without this the search dies the moment a merchant edits the hero.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target || document); });
  document.addEventListener('shopify:section:unload', function (e) {
    var t = e.target;
    if (!t) return;
    var list = t.matches && t.matches('[data-ss]') ? [t] : t.querySelectorAll('[data-ss]');
    [].forEach.call(list, function (n) {
      if (typeof n.__ssTeardown === 'function') n.__ssTeardown();
    });
  });

  // Exposed for the test harness to exercise the ranking engine directly.
  window.SiteSearch = { fold: fold, makeEntry: makeEntry, search: search, parseQuery: parseQuery, osaWithin: osaWithin };
})();
